import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

// Platform > Page access > Add people (04/11/2026: "add a bulk function to
// page access to bulk add people"). Paste a list of names or emails (one per
// line, or separated by commas / semicolons) and/or pick people by search,
// tick the pages, and give (or take away) them all in one save. Saved through
// the same PUT /bulk as the grid, so each change is in the history and a Page
// access manager is still held to the pages they may set.

const errorText = (err) => err?.response?.data?.error || err?.message || 'Something went wrong.';
const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

function PageAccessAddPeople({ pages, directory }) {
  const queryClient = useQueryClient();
  const [pasted, setPasted] = useState('');
  const [picked, setPicked] = useState(() => new Map()); // oid -> person
  const [leftOut, setLeftOut] = useState(() => new Set()); // pasted people taken off with ×
  const [search, setSearch] = useState('');
  const [pageIds, setPageIds] = useState(() => new Set());
  const [action, setAction] = useState('give'); // 'give' | 'remove'
  const [result, setResult] = useState(null);

  const grantsQuery = useQuery({
    queryKey: ['pagePermissions', 'grants'],
    queryFn: async () => (await api.get('/api/page-permissions/grants')).data.grants,
    staleTime: 0,
    retry: false,
  });
  const held = useMemo(
    () => new Set((grantsQuery.data ?? []).map((g) => `${g.userOid.toLowerCase()}|${g.pageId}`)),
    [grantsQuery.data]
  );

  const people = useMemo(() => {
    const out = [];
    for (const p of directory.data?.people ?? []) {
      if (!p.entra_user_id || !p.display_name) continue;
      out.push({
        userOid: p.entra_user_id.toLowerCase(),
        displayName: p.display_name,
        email: p.email || '',
        detail: p.job_title || p.department || p.email || null,
      });
    }
    return out.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [directory.data]);

  // Each pasted entry: an email, else the exact name, else a name that only
  // one person's name contains. Several matches or none are shown, not guessed.
  const parsed = useMemo(() => {
    const entries = [...new Set(pasted.split(/[\n,;\t]+/).map((s) => s.trim()).filter(Boolean))];
    const found = [];
    const missing = [];
    const several = [];
    for (const entry of entries) {
      const n = norm(entry);
      let hits = people.filter((p) => norm(p.email) === n);
      if (!hits.length) hits = people.filter((p) => norm(p.displayName) === n);
      if (!hits.length) hits = people.filter((p) => norm(p.displayName).includes(n));
      if (hits.length === 1) found.push(hits[0]);
      else if (!hits.length) missing.push(entry);
      else several.push({ entry, names: hits.slice(0, 4).map((h) => h.displayName), more: hits.length > 4 });
    }
    return { found, missing, several };
  }, [pasted, people]);

  const selected = useMemo(() => {
    const byOid = new Map(picked);
    for (const p of parsed.found) if (!leftOut.has(p.userOid)) byOid.set(p.userOid, p);
    return [...byOid.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [picked, parsed.found, leftOut]);

  const q = norm(search);
  const searchHits = q
    ? people.filter((p) => !selected.some((s) => s.userOid === p.userOid) && (norm(p.displayName).includes(q) || norm(p.email).includes(q) || norm(p.detail).includes(q))).slice(0, 8)
    : [];

  const pick = (p) => {
    setResult(null);
    setPicked((prev) => new Map(prev).set(p.userOid, p));
    setLeftOut((prev) => { const next = new Set(prev); next.delete(p.userOid); return next; });
    setSearch('');
  };
  // × takes someone off whether they were picked or pasted.
  const unpick = (oid) => {
    setResult(null);
    setPicked((prev) => { const next = new Map(prev); next.delete(oid); return next; });
    setLeftOut((prev) => new Set(prev).add(oid));
  };
  const togglePage = (id) => {
    setResult(null);
    setPageIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  // Only what would actually change.
  const changes = useMemo(() => {
    const out = [];
    for (const person of selected) {
      for (const page of pages) {
        if (!pageIds.has(page.id)) continue;
        const has = held.has(`${person.userOid}|${page.id}`);
        if (action === 'give' && !has) {
          out.push({ pageId: page.id, userOid: person.userOid, displayName: person.displayName, level: page.levels[page.levels.length - 1] || 'edit' });
        } else if (action === 'remove' && has) {
          out.push({ pageId: page.id, userOid: person.userOid, displayName: person.displayName, level: 'none' });
        }
      }
    }
    return out;
  }, [selected, pages, pageIds, held, action]);

  const save = useMutation({
    mutationFn: async () => (await api.put('/api/page-permissions/bulk', { changes })).data,
    onSuccess: (data) => {
      setResult(data);
      if (!data.failed?.length) { setPasted(''); setPicked(new Map()); setLeftOut(new Set()); }
    },
    onError: (err) => setResult({ error: errorText(err) }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['pagePermissions'] }),
  });

  const input = 'w-full rounded-md border border-gray-300 px-2.5 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
  const loading = directory.isLoading || grantsQuery.isLoading;

  return (
    <div className="mb-6 grid gap-4 lg:grid-cols-2">
      {/* ---- who ---- */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="text-base font-semibold text-gray-900">1. People</h2>
        <p className="mb-2 text-sm text-gray-500">Paste names or emails, one per line or separated by commas, and/or find people below.</p>
        <textarea
          value={pasted}
          onChange={(e) => { setPasted(e.target.value); setResult(null); }}
          rows={6}
          placeholder={'Ashley Wass\nkevin.duplessis@nanofibre.co.uk\nShane Grobler'}
          className={input}
        />
        {(parsed.missing.length > 0 || parsed.several.length > 0) && (
          <div className="mt-2 space-y-1 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
            {parsed.missing.length > 0 && <p><span className="font-semibold">Not found:</span> {parsed.missing.join(', ')}</p>}
            {parsed.several.map((s) => (
              <p key={s.entry}><span className="font-semibold">“{s.entry}” matches several:</span> {s.names.join(', ')}{s.more ? '…' : ''} — use the full name or email, or find them below.</p>
            ))}
          </div>
        )}

        <div className="relative mt-3">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={loading ? 'Loading the staff directory…' : 'Find someone by name, email or role…'}
            className={`${input} pl-8`}
          />
          {searchHits.length > 0 && (
            <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
              {searchHits.map((p) => (
                <li key={p.userOid}>
                  <button type="button" onClick={() => pick(p)} className="block w-full px-3 py-2 text-left hover:bg-gray-50">
                    <span className="block text-sm font-medium text-gray-900">{p.displayName}</span>
                    {p.detail && <span className="block text-xs text-gray-500">{p.detail}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-3 text-sm font-semibold text-gray-900">{selected.length} {selected.length === 1 ? 'person' : 'people'} selected</p>
        {selected.length > 0 && (
          <div className="mt-2 flex max-h-48 flex-wrap gap-1.5 overflow-auto">
            {selected.map((p) => (
              <span key={p.userOid} className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 py-0.5 pl-2.5 pr-1 text-xs text-gray-800">
                {p.displayName}
                <button type="button" onClick={() => unpick(p.userOid)} className="rounded-full p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700" aria-label={`Remove ${p.displayName}`}>
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
        {directory.isError && <p className="mt-2 text-sm text-amber-700">The staff directory couldn’t be loaded, so nobody can be matched right now.</p>}
      </div>

      {/* ---- which pages, and save ---- */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="text-base font-semibold text-gray-900">2. Pages</h2>
        <div className="mb-3 mt-2 inline-flex rounded-lg border border-gray-200 bg-white p-1 text-sm">
          {[['give', 'Give access'], ['remove', 'Take access away']].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => { setAction(key); setResult(null); }}
              className={`rounded-md px-3 py-1.5 font-medium ${action === key ? 'bg-brand text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <ul className="space-y-1.5">
          {pages.map((page) => (
            <li key={page.id}>
              <label className="flex items-start gap-2 text-sm text-gray-800" title={page.description}>
                <input
                  type="checkbox"
                  checked={pageIds.has(page.id)}
                  onChange={() => togglePage(page.id)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
                />
                <span>{page.label}</span>
              </label>
            </li>
          ))}
        </ul>

        <div className="mt-4 border-t border-gray-100 pt-3">
          <p className="mb-2 text-sm text-gray-600">
            {selected.length === 0 || pageIds.size === 0
              ? 'Pick people and at least one page.'
              : changes.length === 0
                ? (action === 'give' ? 'Everyone selected already has those pages.' : 'Nobody selected has those pages.')
                : `${changes.length} ${changes.length === 1 ? 'change' : 'changes'}: ${action === 'give' ? 'giving' : 'taking away'} ${pageIds.size} ${pageIds.size === 1 ? 'page' : 'pages'} for ${new Set(changes.map((c) => c.userOid)).size} ${new Set(changes.map((c) => c.userOid)).size === 1 ? 'person' : 'people'}.`}
          </p>
          <button
            type="button"
            disabled={changes.length === 0 || save.isPending || changes.length > 2000}
            onClick={() => save.mutate()}
            className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : action === 'give' ? 'Give access' : 'Take access away'}
          </button>
          {changes.length > 2000 && <p className="mt-2 text-sm text-red-700">That’s more than 2,000 changes; split it into smaller lots.</p>}
          {result?.error && <p className="mt-2 text-sm text-red-700">Couldn’t save: {result.error}</p>}
          {result && !result.error && (
            <p className={`mt-2 text-sm ${result.failed?.length ? 'text-amber-800' : 'text-green-700'}`}>
              Saved {result.changed} {result.changed === 1 ? 'change' : 'changes'}.
              {result.failed?.length > 0 && <> {result.failed.length} couldn’t be saved: {result.failed.slice(0, 3).map((f) => `${f.displayName || f.userOid} (${f.error})`).join('; ')}{result.failed.length > 3 ? '…' : ''}</>}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default PageAccessAddPeople;
